@if (vm$ | async; as vm) {
  <div class="acct">
    <div class="shell">
      <div class="head">
        <div class="title-wrap">
          <h1 class="title">My account</h1>
          @if (vm.fullName) {
            <p class="subtitle">
              Welcome back, {{ vm.fullName }}
            </p>
          }
        </div>
        <button class="logout btn-ghost" (click)="logout()">Logout</button>
      </div>
      <div class="body">
        <aside class="nav">
          @for (section of sections; track section) {
            <button
              type="button"
              class="tab"
              [class.active]="activeSection === section.key"
              [attr.aria-current]="activeSection === section.key ? 'page' : null"
              (click)="setSection(section.key)"
              >
              {{ section.label }}
            </button>
          }
        </aside>
        <div class="content">
          <!-- PROFILE -->
          @if (activeSection === 'profile') {
            <section
              class="sec"
              aria-labelledby="profile-section-title"
              >
              <div class="sec-head">
                <div>
                  <h2>Profile details</h2>
                  <p class="copy">View your account information.</p>
                </div>
              </div>
              <div class="grid">
                <button
                  type="button"
                  class="card hi card-btn"
                  (click)="openEditNameModal()"
                  >
                  <div class="card-top">
                    <span class="k">Name</span>
                    <i class="fa-solid fa-pen-to-square" aria-hidden="true"></i>
                  </div>
                  <p class="v">
                    {{ getDisplayFullName(vm) || "Not provided" }}
                  </p>
                </button>
                <div class="card">
                  <span class="k">Email</span>
                  <p class="v mono">
                    {{ vm.user?.email || "Not provided" }}
                  </p>
                </div>
                <button
                  type="button"
                  class="card card-btn"
                  (click)="openEditPhoneModal()"
                  >
                  <div class="card-top">
                    <span class="k">Phone</span>
                    <i class="fa-solid fa-pen-to-square" aria-hidden="true"></i>
                  </div>
                  <p class="v">
                    {{ getDisplayPhone(vm) || "Not provided" }}
                  </p>
                </button>
              </div>
            </section>
            <axis-modal
              [show]="showEditNameModal"
              (closed)="closeEditNameModal()"
              [closeOnBackdrop]="!profileSaving"
              [disableEscape]="profileSaving"
              style="--modal-width: 560px"
              >
              <div class="dlg">
                <div class="mh">
                  <div>
                    <h3>Update name</h3>
                    <p>Edit your first and last name.</p>
                  </div>
                  <button
                    type="button"
                    class="info-modal__close"
                    (click)="closeEditNameModal()"
                    [disabled]="profileSaving"
                    aria-label="Close update name modal"
                    >
                    <i class="fa-solid fa-xmark" aria-hidden="true"></i>
                  </button>
                </div>
                <div class="mb">
                  @if (profileSuccessMsg && showEditNameModal) {
                    <div
                      class="feedback success"
                      aria-live="polite"
                      >
                      {{ profileSuccessMsg }}
                    </div>
                  }
                  @if (profileErrorMsg && showEditNameModal) {
                    <div
                      class="feedback error"
                      aria-live="assertive"
                      >
                      {{ profileErrorMsg }}
                    </div>
                  }
                  <axis-form
                    class="ax-form ax-modal"
                    [title]="''"
                    [buttonText]="profileSaving ? 'Updating…' : 'Update name'"
                    [inputs]="updateNameInputs"
                    [showLabels]="true"
                    [submitting]="profileSaving"
                  [model]="{
                    firstName:
                      profileNameOverride?.fName ||
                      (vm$ | async)?.user?.fName ||
                      '',
                    lastName:
                      profileNameOverride?.lName ||
                      (vm$ | async)?.user?.lName ||
                      '',
                  }"
                    [submitOverride]="submitUpdateName"
                  ></axis-form>
                </div>
              </div>
            </axis-modal>
            <axis-modal
              [show]="showEditPhoneModal"
              (closed)="closeEditPhoneModal()"
              [closeOnBackdrop]="!profileSaving"
              [disableEscape]="profileSaving"
              style="--modal-width: 560px"
              >
              <div class="dlg">
                <div class="mh">
                  <div>
                    <h3>Update mobile number</h3>
                    <p>Edit the mobile number on your account.</p>
                  </div>
                  <button
                    type="button"
                    class="info-modal__close"
                    (click)="closeEditPhoneModal()"
                    [disabled]="profileSaving"
                    aria-label="Close update phone modal"
                    >
                    <i class="fa-solid fa-xmark" aria-hidden="true"></i>
                  </button>
                </div>
                <div class="mb">
                  @if (profileSuccessMsg && showEditPhoneModal) {
                    <div
                      class="feedback success"
                      aria-live="polite"
                      >
                      {{ profileSuccessMsg }}
                    </div>
                  }
                  @if (profileErrorMsg && showEditPhoneModal) {
                    <div
                      class="feedback error"
                      aria-live="assertive"
                      >
                      {{ profileErrorMsg }}
                    </div>
                  }
                  <axis-form
                    class="ax-form ax-modal"
                    [title]="''"
                  [buttonText]="
                    profileSaving ? 'Updating…' : 'Update mobile number'
                  "
                    [inputs]="updatePhoneInputs"
                    [showLabels]="true"
                    [submitting]="profileSaving"
                  [model]="{
                    phoneNumber:
                      profilePhoneOverride || (vm$ | async)?.user?.phone || '',
                  }"
                    [submitOverride]="submitUpdatePhone"
                  ></axis-form>
                </div>
              </div>
            </axis-modal>
          }
          <!-- SECURITY -->
          @if (activeSection === 'security') {
            <section
              class="sec"
              aria-labelledby="security-section-title"
              >
              <div class="sec-head">
                <div>
                  <h2>Security</h2>
                  <p class="copy">
                    Manage your password and keep your account secure.
                  </p>
                </div>
              </div>
              <div class="settings">
                <h3 class="settings-title">Account security</h3>
                <button
                  type="button"
                  class="setting"
                  (click)="openChangePasswordModal()"
                  >
                  <div class="setting-left">
                    <span class="setting-label">Password</span>
                    <span class="setting-hint">
                      Change your password to keep your account secure
                    </span>
                  </div>
                  <div class="setting-right">
                    <span class="setting-value">••••••••</span>
                    <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
                  </div>
                </button>
              </div>
            </section>
          }
          <axis-modal
            [show]="showChangePasswordModal"
            (closed)="closeChangePasswordModal()"
            [closeOnBackdrop]="!passwordSaving"
            [disableEscape]="passwordSaving"
            style="--modal-width: 560px"
            >
            <div class="dlg">
              <div class="mh">
                <div>
                  <h3>Change password</h3>
                  <p>Enter your current password and choose a new one.</p>
                </div>
                <button
                  type="button"
                  class="info-modal__close"
                  (click)="closeChangePasswordModal()"
                  [disabled]="passwordSaving"
                  aria-label="Close change password modal"
                  >
                  <i class="fa-solid fa-xmark" aria-hidden="true"></i>
                </button>
              </div>
              <div class="mb">
                @if (passwordSuccessMsg) {
                  <div
                    class="feedback success"
                    aria-live="polite"
                    >
                    {{ passwordSuccessMsg }}
                  </div>
                }
                @if (passwordErrorMsg) {
                  <div
                    class="feedback error"
                    aria-live="assertive"
                    >
                    {{ passwordErrorMsg }}
                  </div>
                }
                <axis-form
                  class="ax-form ax-modal"
                  [title]="''"
                  [buttonText]="passwordSaving ? 'Updating…' : 'Update password'"
                  [inputs]="changePasswordInputs"
                  [showLabels]="true"
                  [submitting]="passwordSaving"
                  [submitOverride]="submitChangePassword"
                ></axis-form>
              </div>
            </div>
          </axis-modal>
          <!-- ADDRESSES -->
          @if (activeSection === 'addresses') {
            <section
              class="sec"
              aria-labelledby="addresses-section-title"
              >
              <div class="sec-head">
                <div>
                  <h2>Saved addresses</h2>
                  <p class="copy">
                    View and manage the delivery addresses saved to your account.
                  </p>
                </div>
              </div>
              <!-- Loading -->
              @if (addressesLoading$ | async) {
                <div class="loading">
                  <p>Loading your saved addresses…</p>
                </div>
              }
              <!-- Error -->
              @if (!(addressesLoading$ | async) && addressesError) {
                <div
                  class="empty"
                  >
                  <h3>Unable to load addresses</h3>
                  <p class="muted">{{ addressesError }}</p>
                  <button
                    class="btn-primary"
                    type="button"
                    (click)="refreshAddresses()"
                    >
                    Try again
                  </button>
                </div>
              }
              <!-- Empty -->
              @if (
                !(addressesLoading$ | async) &&
                !addressesError &&
                addressesLoaded &&
                addresses.length === 0
                ) {
                <div class="empty">
                  <h3>No saved addresses yet</h3>
                </div>
              }
              @if (addressActionMessage) {
                <div
                  class="feedback success"
                  aria-live="polite"
                  >
                  {{ addressActionMessage }}
                </div>
              }
              @if (addressActionError) {
                <div
                  class="feedback error"
                  aria-live="assertive"
                  >
                  {{ addressActionError }}
                </div>
              }
              <!-- Has addresses -->
              @if (
                !(addressesLoading$ | async) &&
                !addressesError &&
                addresses.length > 0
                ) {
                <div
                  class="addr-list"
                  >
                  <div class="actions">
                    <button
                      class="btn-ghost"
                      type="button"
                      (click)="refreshAddresses()"
                      [disabled]="addressesLoading$ | async"
                      >
                      {{ (addressesLoading$ | async) ? "Refreshing…" : "Refresh" }}
                    </button>
                  </div>
                  @for (address of addresses; track trackByAddress($index, address)) {
                    <article
                      class="addr"
                      >
                      <div class="addr-body">
                        @if (address.isDefault) {
                          <div class="addr-meta">
                            <span class="chip ok"
                              >Default
                            </span>
                          </div>
                        }
                        <p class="addr-line">
                          {{ address.line1 }}
                          @if (address.line2) {
                            <span>, {{ address.line2 }}</span>
                          }
                        </p>
                        <p class="addr-line">
                          {{ address.city }}
                          @if (address.county) {
                            <span>, {{ address.county }}</span>
                          }
                        </p>
                        <p class="addr-line">
                          {{ address.postcode }}
                        </p>
                        @if (address.country) {
                          <p class="addr-line">
                            {{ address.country }}
                          </p>
                        }
                      </div>
                      <div class="addr-actions">
                        @if (!address.isDefault) {
                          <button
                            class="btn-primary"
                            type="button"
                            (click)="setDefaultAddress(address)"
                            [disabled]="isAddressBusy(address)"
                            >
                            {{
                            isDefaultingAddress(address)
                            ? "Saving…"
                            : "Set as default"
                            }}
                          </button>
                        }
                        @if (address.isDefault) {
                          <button
                            class="btn-ghost"
                            type="button"
                            disabled
                            >
                            Default
                          </button>
                        }
                        <button
                          class="btn-ghost"
                          type="button"
                          (click)="openDeleteAddressModal(address)"
                          [disabled]="isAddressBusy(address)"
                          >
                          Delete
                        </button>
                      </div>
                    </article>
                  }
                </div>
              }
            </section>
          }
          <!-- ORDERS -->
          @if (activeSection === 'orders') {
            <section
              class="sec"
              aria-labelledby="orders-section-title"
              >
              <div class="sec-head">
                <div>
                  <h2>Your orders</h2>
                  <p class="copy">
                    View your recent and past orders in one place.
                  </p>
                </div>
                <button
                  class="btn-ghost"
                  type="button"
                  (click)="loadOrders()"
                  [disabled]="ordersLoading$ | async"
                  >
                  {{ (ordersLoading$ | async) ? "Loading…" : "Refresh" }}
                </button>
              </div>
              <!-- Loading -->
              @if (ordersLoading$ | async) {
                <div class="loading">
                  <p>Loading your order history…</p>
                </div>
              }
              <!-- Error -->
              @if (!(ordersLoading$ | async) && ordersError) {
                <div
                  class="empty"
                  >
                  <h3>Unable to load orders</h3>
                  <p class="muted">{{ ordersError }}</p>
                  <button class="btn-primary" type="button" (click)="loadOrders()">
                    Try again
                  </button>
                </div>
              }
              <!-- Empty -->
              @if (
                !(ordersLoading$ | async) &&
                !ordersError &&
                ordersLoaded &&
                orders.length === 0
                ) {
                <div
                  class="empty"
                  >
                  <h3>No orders to show yet</h3>
                  <p class="muted">When you place an order, it will appear here.</p>
                </div>
              }
              <!-- List -->
              @if (
                !(ordersLoading$ | async) &&
                !ordersError &&
                ordersLoaded &&
                orders.length > 0
                ) {
                <div
                  class="orders"
                  >
                  @for (order of orders; track trackByOrder($index, order)) {
                    <article
                      class="order"
                      >
                      <div class="order-top">
                        <div>
                          <span class="k">Order number</span>
                          <p class="v mono">
                            {{ order.orderNumber || "—" }}
                          </p>
                        </div>
                        <div class="status-badge">
                          {{ order.status || "Unknown" }}
                        </div>
                      </div>
                      <div class="grid grid-orders">
                        <div class="card">
                          <span class="k">Date / time</span>
                          <p class="v">{{ order.dateTime || "—" }}</p>
                        </div>
                        <div class="card">
                          <span class="k">Total</span>
                          <p class="v">{{ order.total || "—" }}</p>
                        </div>
                        <div class="card">
                          <span class="k">Type</span>
                          <p class="v">
                            {{ order.fulfilmentType || "—" }}
                          </p>
                        </div>
                        <div class="card">
                          <span class="k">Payment</span>
                          <p class="v">
                            {{ order.paymentStatus || "—" }}
                          </p>
                        </div>
                      </div>
                      <div class="order-actions">
                        <button
                          class="btn-primary"
                          type="button"
                          (click)="trackOrder(order)"
                          [disabled]="!canTrackOrder(order)"
                          >
                          Track order
                        </button>
                        <button
                          class="btn-ghost"
                          type="button"
                          (click)="viewOrder(order)"
                          >
                          View order
                        </button>
                      </div>
                    </article>
                  }
                </div>
              }
            </section>
          }
        </div>
      </div>
    </div>
    <axis-modal
      [show]="showViewOrderModal"
      (closed)="closeViewOrderModal()"
      [closeOnBackdrop]="!viewOrderLoading"
      [disableEscape]="viewOrderLoading"
      style="--modal-width: 720px"
      >
      <div class="dlg">
        <div class="mh">
          <div>
            <h3>Order details</h3>
            @if (selectedOrderDetails?.orderNumber) {
              <p>
                Order {{ selectedOrderDetails?.orderNumber }}
              </p>
            }
          </div>
          <button
            type="button"
            class="info-modal__close"
            (click)="closeViewOrderModal()"
            [disabled]="viewOrderLoading"
            aria-label="Close order details modal"
            >
            <i class="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
        </div>
        <div class="mb">
          @if (viewOrderLoading) {
            <div class="loading">
              <p>Loading order details…</p>
            </div>
          }
          @if (!viewOrderLoading && viewOrderError) {
            <div class="empty">
              <h3>Unable to load order</h3>
              <p class="muted">{{ viewOrderError }}</p>
            </div>
          }
          @if (
            !viewOrderLoading &&
            !viewOrderError &&
            selectedOrderDetails; as details
            ) {
            <div class="summary">
              <div class="summary-grid">
                <div class="card">
                  <span class="k">Status</span>
                  <p class="v">{{ details.status || "—" }}</p>
                </div>
                <div class="card">
                  <span class="k">Payment</span>
                  <p class="v">{{ details.paymentStatus || "—" }}</p>
                </div>
                <div class="card">
                  <span class="k">Type</span>
                  <p class="v">{{ details.fulfilmentType || "—" }}</p>
                </div>
                <div class="card">
                  <span class="k">Date / time</span>
                  <p class="v">{{ details.dateTime || "—" }}</p>
                </div>
              </div>
            </div>
            <div class="items">
              <h4>Items</h4>
              @for (item of details.items; track item) {
                <article class="oi">
                  <div class="oi-top">
                    <div>
                      <p class="oi-name">
                        {{ item.quantity }} × {{ item.name }}
                      </p>
                      @if (item.status) {
                        <p class="oi-status">
                          Status: {{ item.status }}
                        </p>
                      }
                    </div>
                    <p class="oi-price">{{ item.price }}</p>
                  </div>
                  @if (item.extras.length > 0) {
                    <ul class="oi-extras">
                      @for (extra of item.extras; track extra) {
                        <li>
                          +
                          {{
                          extra.quantity && extra.quantity > 1
                          ? extra.quantity + " × "
                          : ""
                          }}{{ extra.label }}
                          @if (extra.price) {
                            <span>({{ extra.price }})</span>
                          }
                        </li>
                      }
                    </ul>
                  }
                  @if (item.note) {
                    <p class="oi-note">
                      Note: {{ item.note }}
                    </p>
                  }
                  @if (item.tenantComment) {
                    <p class="oi-note">
                      Restaurant note: {{ item.tenantComment }}
                    </p>
                  }
                </article>
              }
            </div>
            @if (details.total && details.total !== '—') {
              <div
                class="total-card"
                >
                <div class="total-line grand">
                  <span>Total paid</span>
                  <span>{{ details.total }}</span>
                </div>
              </div>
            }
          }
        </div>
      </div>
    </axis-modal>
    <axis-modal
      [show]="showDeleteAddressModal"
      (closed)="closeDeleteAddressModal()"
      [closeOnBackdrop]="!isDeleteModalBusy"
      [disableEscape]="isDeleteModalBusy"
      style="--modal-width: 520px"
      >
      <div class="dlg">
        <div class="mh">
          <div>
            <h3>Delete address</h3>
            <p>Are you sure you want to remove this saved address?</p>
          </div>
          <button
            type="button"
            class="info-modal__close"
            (click)="closeDeleteAddressModal()"
            [disabled]="isDeleteModalBusy"
            aria-label="Close delete address modal"
            >
            <i class="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
        </div>
        <div class="mb">
          @if (pendingDeleteAddress) {
            <div
              class="card hi"
              >
              <p class="v">
                {{ pendingDeleteAddress.line1 }}
                @if (pendingDeleteAddress.line2) {
                  <span>
                    , {{ pendingDeleteAddress.line2 }}
                  </span>
                }
              </p>
              <p class="muted">
                {{ pendingDeleteAddress.city }}
                @if (pendingDeleteAddress.county) {
                  <span>
                    , {{ pendingDeleteAddress.county }}
                  </span>
                }
              </p>
              <p class="muted">{{ pendingDeleteAddress.postcode }}</p>
              @if (pendingDeleteAddress.country) {
                <p class="muted">
                  {{ pendingDeleteAddress.country }}
                </p>
              }
            </div>
          }
          <div class="addr-actions">
            <button
              class="btn-ghost"
              type="button"
              (click)="closeDeleteAddressModal()"
              [disabled]="isDeleteModalBusy"
              >
              Cancel
            </button>
            <button
              class="btn-primary"
              type="button"
              (click)="confirmDeleteAddress()"
              [disabled]="isDeleteModalBusy"
              >
              {{ isDeleteModalBusy ? "Deleting…" : "Delete address" }}
            </button>
          </div>
        </div>
      </div>
    </axis-modal>
  </div>
} @else {
  <div class="loading">
    <p>Loading your account…</p>
  </div>
}

